/**
 * Budget Operations
 *
 * Handles all budget-related database operations
 */

import type {
  Budget,
  BudgetCreateData,
  BudgetUpdateData,
  BudgetSettings
} from '../../types/budget';
import type { BudgetData, BudgetStatus } from '../../types';
import type { Transaction } from '../../types';
import { query, queryOne, execute, transaction, saveDB } from '../../db';
import { validateBudget } from '../validation';
import {
  parseMonthRange,
  filterTransactionsByMonth,
  calculateSpendingByCategory,
  calculateBudgetStatus,
  groupBudgetsByType,
  calculateUnbudgetedSpending,
  getBudgetedCategories
} from '../../budget/calculations';

/**
 * Get all budgets with their settings
 */
export async function getAll(): Promise<BudgetData> {
  try {
    const budgetRows = query<any>('SELECT * FROM budgets ORDER BY name');
    const settings = await getSettings();

    // Fetch all categories in one query (fix N+1 pattern)
    const allCategories = query<{ budget_id: string; category: string }>(
      'SELECT budget_id, category FROM budget_categories'
    );

    // Group categories by budget_id
    const categoriesByBudget = new Map<string, string[]>();
    for (const cat of allCategories) {
      if (!categoriesByBudget.has(cat.budget_id)) {
        categoriesByBudget.set(cat.budget_id, []);
      }
      categoriesByBudget.get(cat.budget_id)!.push(cat.category);
    }

    const budgets: Budget[] = budgetRows.map(row => ({
      id: row.id,
      name: row.name,
      categories: categoriesByBudget.get(row.id) || [],
      limit: row.limit_amount,
      type: row.type,
      allowRollover: !!row.allow_rollover,
      resetRolloverYearly: !!row.reset_rollover_yearly,
      isFixedExpense: !!row.is_fixed_expense,
      rolloverAmount: row.rollover_amount,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    }));

    return {
      version: settings.version,
      budgets,
      settings
    };
  } catch (error) {
    console.error('[budgetOperations] Error loading budgets:', error);
    return {
      version: 3,
      budgets: [],
      settings: {
        version: 3,
        autoResetRolloverAtYearEnd: false
      }
    };
  }
}

/**
 * Create a new budget
 */
export async function create(data: BudgetCreateData): Promise<{ success: boolean; budget?: Budget; error?: string }> {
  // Validation using utility function
  const validation = validateBudget(data);
  if (!validation.valid) {
    return { success: false, error: validation.errors[0].message };
  }

  try {
    // Check for duplicate
    const existing = queryOne<{ count: number }>(
      'SELECT COUNT(*) as count FROM budgets WHERE name = ? AND type = ?',
      [data.name, data.type]
    );
    if (existing && existing.count > 0) {
      return { success: false, error: 'Budget with this name and type already exists' };
    }

    const budgetId = `budget_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    const now = new Date().toISOString();

    // Insert budget
    execute(
      `INSERT INTO budgets (id, name, type, limit_amount, allow_rollover, reset_rollover_yearly, is_fixed_expense, rollover_amount, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        budgetId,
        data.name.trim(),
        data.type,
        data.limit,
        data.allowRollover ? 1 : 0,
        data.resetRolloverYearly ? 1 : 0,
        data.isFixedExpense ? 1 : 0,
        0, // rollover_amount starts at 0
        now,
        now
      ]
    );

    // Insert categories
    for (const category of data.categories) {
      execute(
        'INSERT INTO budget_categories (budget_id, category) VALUES (?, ?)',
        [budgetId, category]
      );
    }

    await saveDB();

    const budget: Budget = {
      id: budgetId,
      name: data.name.trim(),
      categories: data.categories,
      limit: data.limit,
      type: data.type,
      allowRollover: data.allowRollover || false,
      resetRolloverYearly: data.resetRolloverYearly || false,
      isFixedExpense: data.isFixedExpense || false,
      rolloverAmount: 0,
      createdAt: now,
      updatedAt: now
    };

    return { success: true, budget };
  } catch (error) {
    console.error('[budgetOperations] Error creating budget:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Failed to create budget' };
  }
}

/**
 * Update an existing budget
 */
export async function update(
  name: string,
  type: string,
  updates: BudgetUpdateData
): Promise<{ success: boolean; budget?: Budget; error?: string }> {
  try {
    // Find budget
    const existing = queryOne<{ id: string }>(
      'SELECT id FROM budgets WHERE name = ? AND type = ?',
      [name, type]
    );
    if (!existing) {
      return { success: false, error: 'Budget not found' };
    }

    const now = new Date().toISOString();
    const setParts: string[] = [];
    const values: any[] = [];

    // Build UPDATE query dynamically
    if (updates.name !== undefined) {
      setParts.push('name = ?');
      values.push(updates.name);
    }
    if (updates.limit !== undefined) {
      setParts.push('limit_amount = ?');
      values.push(updates.limit);
    }
    if (updates.allowRollover !== undefined) {
      setParts.push('allow_rollover = ?');
      values.push(updates.allowRollover ? 1 : 0);
    }
    if (updates.resetRolloverYearly !== undefined) {
      setParts.push('reset_rollover_yearly = ?');
      values.push(updates.resetRolloverYearly ? 1 : 0);
    }
    if (updates.isFixedExpense !== undefined) {
      setParts.push('is_fixed_expense = ?');
      values.push(updates.isFixedExpense ? 1 : 0);
    }
    if (updates.rolloverAmount !== undefined) {
      setParts.push('rollover_amount = ?');
      values.push(updates.rolloverAmount);
    }

    setParts.push('updated_at = ?');
    values.push(now);
    values.push(existing.id);

    if (setParts.length > 1) { // More than just updated_at
      execute(
        `UPDATE budgets SET ${setParts.join(', ')} WHERE id = ?`,
        values
      );
    }

    // Update categories if provided
    if (updates.categories) {
      execute('DELETE FROM budget_categories WHERE budget_id = ?', [existing.id]);
      for (const category of updates.categories) {
        execute(
          'INSERT INTO budget_categories (budget_id, category) VALUES (?, ?)',
          [existing.id, category]
        );
      }
    }

    await saveDB();

    // Fetch updated budget
    const data = await getAll();
    const budget = data.budgets.find(b => b.id === existing.id);

    return { success: true, budget };
  } catch (error) {
    console.error('[budgetOperations] Error updating budget:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Failed to update budget' };
  }
}

/**
 * Delete a budget
 */
export async function deleteBudget(name: string, type: string): Promise<{ success: boolean; error?: string }> {
  try {
    execute(
      'DELETE FROM budgets WHERE name = ? AND type = ?',
      [name, type]
    );

    // Categories are automatically deleted via CASCADE
    await saveDB();

    return { success: true };
  } catch (error) {
    console.error('[budgetOperations] Error deleting budget:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Failed to delete budget' };
  }
}

/**
 * Get budget status for a month
 */
export async function getStatus(
  month: string,
  transactions: Transaction[]
): Promise<{
  monthly: BudgetStatus[];
  quarterly: BudgetStatus[];
  yearly: BudgetStatus[];
  unbudgeted: BudgetStatus[];
}> {
  try {
    const budgetData = await getAll();

    // Parse month range using utility
    const range = parseMonthRange(month);

    // Filter transactions for the month using utility
    const monthTransactions = filterTransactionsByMonth(transactions, range);

    // Calculate spending by category using utility
    const spentByCategory = calculateSpendingByCategory(monthTransactions);

    // Calculate budget status for each budget using utility
    const budgetStatuses = budgetData.budgets.map(budget =>
      calculateBudgetStatus(budget, spentByCategory)
    );

    // Group budgets by type using utility
    const { monthly, quarterly, yearly } = groupBudgetsByType(budgetStatuses);

    // Calculate unbudgeted spending using utility
    const budgetedCategories = getBudgetedCategories(budgetData.budgets);
    const unbudgetedStatus = calculateUnbudgetedSpending(spentByCategory, budgetedCategories);
    const unbudgeted = unbudgetedStatus ? [unbudgetedStatus] : [];

    return { monthly, quarterly, yearly, unbudgeted };
  } catch (error) {
    console.error('[budgetOperations] Error calculating budget status:', error);
    return { monthly: [], quarterly: [], yearly: [], unbudgeted: [] };
  }
}

/**
 * Update budget rollover amount
 */
export async function updateRollover(
  name: string,
  type: string,
  rolloverAmount: number
): Promise<{ success: boolean; error?: string }> {
  try {
    execute(
      'UPDATE budgets SET rollover_amount = ?, updated_at = ? WHERE name = ? AND type = ?',
      [rolloverAmount, new Date().toISOString(), name, type]
    );

    await saveDB();
    return { success: true };
  } catch (error) {
    console.error('[budgetOperations] Error updating rollover:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Failed to update rollover' };
  }
}

/**
 * Reset budget rollover to zero
 */
export async function resetRollover(name: string, type: string): Promise<{ success: boolean; error?: string }> {
  return updateRollover(name, type, 0);
}

/**
 * Get budget settings
 */
export async function getSettings(): Promise<BudgetSettings> {
  try {
    const row = queryOne<any>('SELECT * FROM budget_settings WHERE id = 1');
    if (!row) {
      return {
        version: 3,
        autoResetRolloverAtYearEnd: false
      };
    }

    return {
      version: row.version || 3,
      autoResetRolloverAtYearEnd: !!row.auto_reset_rollover_at_year_end,
      monthlyIncomeTarget: row.monthly_income_target || undefined
    };
  } catch (error) {
    console.error('[budgetOperations] Error loading budget settings:', error);
    return {
      version: 3,
      autoResetRolloverAtYearEnd: false
    };
  }
}

/**
 * Update budget settings
 */
export async function updateSettings(settings: Partial<BudgetSettings>): Promise<{ success: boolean; settings?: BudgetSettings; error?: string }> {
  try {
    const setParts: string[] = [];
    const values: any[] = [];

    if (settings.version !== undefined) {
      setParts.push('version = ?');
      values.push(settings.version);
    }
    if (settings.autoResetRolloverAtYearEnd !== undefined) {
      setParts.push('auto_reset_rollover_at_year_end = ?');
      values.push(settings.autoResetRolloverAtYearEnd ? 1 : 0);
    }
    if (settings.monthlyIncomeTarget !== undefined) {
      setParts.push('monthly_income_target = ?');
      values.push(settings.monthlyIncomeTarget || null);
    }

    setParts.push('updated_at = ?');
    values.push(new Date().toISOString());

    if (setParts.length > 1) {
      execute(
        `UPDATE budget_settings SET ${setParts.join(', ')} WHERE id = 1`,
        values
      );
      await saveDB();
    }

    const updatedSettings = await getSettings();
    return { success: true, settings: updatedSettings };
  } catch (error) {
    console.error('[budgetOperations] Error updating budget settings:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Failed to update settings' };
  }
}

/**
 * Delete all budgets
 */
export async function deleteAll(): Promise<boolean> {
  try {
    transaction(() => {
      execute('DELETE FROM budget_categories');
      execute('DELETE FROM budgets');
    });
    await saveDB();
    return true;
  } catch (error) {
    console.error('[budgetOperations] Error deleting all budgets:', error);
    return false;
  }
}
